import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './UserSearch.css';

const API = process.env.REACT_APP_API_URL;

const Avatar = ({ username, photoUrl, size = 38 }) => {
  const initial = (username || '?')[0].toUpperCase();
  return photoUrl
    ? <img src={photoUrl} alt={username} className="us-avatar-img" style={{ width: size, height: size }} />
    : <div className="us-avatar-placeholder" style={{ width: size, height: size }}>{initial}</div>;
};

const UserSearch = ({ onClose, onFollowChanged, onViewProfile }) => {
  const { t } = useTranslation();
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [followState, setFollowState] = useState({}); // { [username]: 'following'|'pending'|'not_following' }
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const token = () => localStorage.getItem('access_token');

  useEffect(() => { inputRef.current?.focus(); }, []);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/users/search/?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setResults(r.data);
      // Fetch follow status for each result
      const states = {};
      await Promise.all(r.data.map(async (u) => {
        try {
          const s = await axios.get(`${API}/following/${u.username}/`, {
            headers: { Authorization: `Bearer ${token()}` },
          });
          states[u.username] = s.data.following ? 'following' : (s.data.pending ? 'pending' : 'not_following');
        } catch {
          states[u.username] = 'not_following';
        }
      }));
      setFollowState(states);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val.trim()), 350);
  };

  const handleFollow = async (username) => {
    const current = followState[username] || 'not_following';
    try {
      if (current === 'not_following') {
        const r = await axios.post(`${API}/follow/${username}/`, {}, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        setFollowState(prev => ({ ...prev, [username]: r.data.status }));
      } else {
        // unfollow / cancel
        await axios.delete(`${API}/follow/${username}/`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        setFollowState(prev => ({ ...prev, [username]: 'not_following' }));
      }
      if (onFollowChanged) onFollowChanged();
    } catch {
      // ignore
    }
  };

  const followLabel = (username) => {
    const s = followState[username];
    if (s === 'following') return t('follow_status_following');
    if (s === 'pending')   return t('follow_status_pending');
    return t('follow_action');
  };

  const followClass = (username) => {
    const s = followState[username];
    if (s === 'following') return 'us-follow-btn us-follow-btn--following';
    if (s === 'pending')   return 'us-follow-btn us-follow-btn--pending';
    return 'us-follow-btn';
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="us-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="us-panel">
        <div className="us-header">
          <h3>{t('search_users_title')}</h3>
          <button className="us-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="us-search-wrap">
          <span className="us-search-icon">🔍</span>
          <input
            ref={inputRef}
            className="us-search-input"
            type="text"
            placeholder={t('search_placeholder')}
            value={query}
            onChange={handleInput}
          />
          {loading && <span className="us-search-spinner" />}
        </div>

        <div className="us-results">
          {!loading && query.length >= 2 && results.length === 0 && (
            <div className="us-empty">{t('no_users_found', { query })}</div>
          )}
          {query.length < 2 && (
            <div className="us-hint">{t('search_hint')}</div>
          )}
          {results.map(user => (
            <div key={user.username} className="us-user-row">
              <div
                className="us-avatar-link"
                style={{ cursor: onViewProfile ? 'pointer' : 'default' }}
                onClick={() => { if (onViewProfile) { onViewProfile(user.username); onClose(); } }}
              >
                <Avatar username={user.username} photoUrl={user.photo_url} size={42} />
              </div>
              <div
                className="us-user-info"
                style={{ cursor: onViewProfile ? 'pointer' : 'default' }}
                onClick={() => { if (onViewProfile) { onViewProfile(user.username); onClose(); } }}
              >
                <span className="us-username">@{user.username}</span>
                {user.full_name && <span className="us-fullname">{user.full_name}</span>}
                {user.bio && <span className="us-bio">{user.bio.slice(0, 80)}{user.bio.length > 80 ? '…' : ''}</span>}
              </div>
              <button
                className={followClass(user.username)}
                onClick={() => handleFollow(user.username)}
              >
                {followLabel(user.username)}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserSearch;
